import { downloadBlob } from '@utils/download.ts';

import { buildCsvIssuesFile, buildFatalCsvIssueFile } from './buildCsvIssuesFile';
import { createCsvBlob, CSV_DEFAULT_DELIMITER } from './constants';
import { parseCaretCsvFields } from './csvFieldHelpers';
import type { CaretCsvDefinition, CaretCsvHandler, CaretCsvImportResult, CaretCsvRowIssue } from './types';

const splitLines = (content: string) => content.split(/\r?\n/).filter((line) => line.trim().length > 0);

const normalizeHeader = (header: string) => header.trim();

export const createCaretCsvHandler = <T>(definition: CaretCsvDefinition<T>): CaretCsvHandler<T> => {
    const delimiter = definition.delimiter ?? CSV_DEFAULT_DELIMITER;
    const issuesFileName = definition.issuesFileName ?? 'import-errors.csv';

    const parseImport = (content: string, existingItems: T[]): CaretCsvImportResult<T> => {
        const lines = splitLines(content);

        if (lines.length <= 1) {
            return {
                items: [],
                rowIssues: [],
                skippedRows: 0,
                fatalError: 'CSV file contains no data rows.',
            };
        }

        if (normalizeHeader(lines[0]) !== normalizeHeader(definition.header)) {
            return {
                items: [],
                rowIssues: [],
                skippedRows: 0,
                fatalError: 'Invalid CSV header. Download the template and ensure column names match.',
            };
        }

        const imported: T[] = [];
        const rowIssues: CaretCsvRowIssue[] = [];
        let skippedRows = 0;

        for (let i = 1; i < lines.length; i++) {
            const rawLine = lines[i];
            const fields = parseCaretCsvFields(rawLine, delimiter);
            const rowNumber = i + 1;

            if (definition.shouldSkipRow?.(fields, existingItems)) {
                skippedRows++;
                continue;
            }

            const validationError = definition.validateRow?.(fields, rowNumber) ?? null;
            if (validationError) {
                rowIssues.push({ rowNumber, error: validationError, rawLine });
                continue;
            }

            const item = definition.parseRow(fields);
            if (!item) {
                rowIssues.push({
                    rowNumber,
                    error: 'Row could not be parsed.',
                    rawLine,
                });
                continue;
            }

            const allExisting = [...existingItems, ...imported];
            if (definition.isDuplicate?.(allExisting, item)) {
                skippedRows++;
                continue;
            }

            imported.push(item);
        }

        return { items: imported, rowIssues, skippedRows };
    };

    const parse = (content: string, existingItems: T[]) => parseImport(content, existingItems).items;

    const build = (items: T[]) => {
        const rows = [definition.header, ...items.map((item) => definition.formatRow(item).join(delimiter))];
        return `${rows.join('\n')}\n`;
    };

    const buildIssuesContent = (issues: CaretCsvRowIssue[]) => buildCsvIssuesFile(delimiter, definition.header, issues);

    const downloadTemplate = () => {
        const content = `${definition.header}\n`;
        downloadBlob(createCsvBlob(content), definition.templateFileName);
    };

    const download = (items: T[], fileName?: string) => {
        const content = build(items);
        downloadBlob(createCsvBlob(content), fileName ?? definition.exportFileName ?? 'export.csv');
    };

    const downloadIssues = (issues: CaretCsvRowIssue[], fileName?: string) => {
        const content = buildIssuesContent(issues);
        downloadBlob(createCsvBlob(content), fileName ?? issuesFileName);
    };

    const readFromFile = async (file: File, existingItems: T[]) => {
        const content = await file.text();
        return parse(content, existingItems);
    };

    const readFromFileWithValidation = async (file: File, existingItems: T[]) => {
        const content = await file.text();
        return parseImport(content, existingItems);
    };

    return {
        parse,
        parseImport,
        build,
        downloadTemplate,
        download,
        downloadIssues,
        readFromFile,
        readFromFileWithValidation,
    };
};

export { buildFatalCsvIssueFile };
